import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { isEmpty } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IBackendCartEntries, ISanityVendorConfigs } from '@rbi-ctg/menu';
import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import { RecentOrderCard } from 'components/recent-order-card';
import { RecentOrderCardCarousel } from 'components/recent-order-card-carousel';
import { RecentOrderCardWithModifiers } from 'components/recent-order-card-with-modifiers';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import useEffectOnce from 'hooks/use-effect-once';
import { useGetSanityImagesForCartEntries } from 'hooks/use-get-sanity-images-for-cart-entries';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useGetMenuItemVendorConfigForCartEntries } from 'state/order/hooks/use-get-menu-item-vendor-config-for-cart-entries';
import { useGetUnavailableItemsFromMenu } from 'state/order/hooks/use-unavailable-cart-entries';
import { isOfferType } from 'state/order/utils';
import { useStoreContext } from 'state/store';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';
import { maybeGetPickerIdFromCartEntry } from 'utils/menu/maybe-get-picker-id-from-cart-entry';
import { priceForItemOptionModifierPlu } from 'utils/menu/price';
import { mapBackendToFrontend } from 'utils/reorder';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { Container } from './styled';
import { IBackendCartEntriesWithAvailability, IRecentItemsProps } from './types';

const RecentItems: FC<IRecentItemsProps> = ({ locationName }) => {
  const getUnavailableItemsFromMenu = useGetUnavailableItemsFromMenu();
  const getMenuItemVendorConfigForCartEntries = useGetMenuItemVendorConfigForCartEntries();
  const [isLoading, setIsLoading] = useState(true);
  const [recentItemIndex, setRecentItemIndex] = useState(0);
  const [recentItems, setRecentItems] = useState<IBackendCartEntriesWithAvailability[]>([]);
  const { getMainMenu, pricingFunction } = useMenuContext();
  const { user, refreshCurrentUser } = useAuthContext();
  const mParticle = useMParticleContext();
  const { location, setStoreLocatorCallbackUrl } = useLocationContext();
  const enableEditRecentItem = useFlag(LaunchDarklyFlag.ENABLE_EDIT_RECENT_ITEM);
  const enableRecentItemWithModifiers = useFlag(
    LaunchDarklyFlag.ENABLE_RECENT_ITEMS_WITH_MODIFIERS
  );
  const sanityImageMap = useGetSanityImagesForCartEntries(user?.uniquePurchasedItems);
  const {
    recent: { pendingRecentItem, setPendingRecentItem },
    addToCart
  } = useOrderContext();
  const { isStoreOpenAndAvailable, prices } = useStoreContext();
  const { vendor } = usePosVendor();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  useEffectOnce(() => {
    refreshCurrentUser();
  });

  const mainMenu = useMemo(() => getMainMenu(), [getMainMenu]);
  const addItemToCart = useCallback(
    (item: IBackendCartEntries) => {
      const curriedPriceItemOptionModifier = (currentItem: IBackendCartEntries) => (
        modifier: IBackendCartEntries
      ) =>
        priceForItemOptionModifierPlu({
          item: currentItem,
          modifier,
          prices,
          vendor
        });

      const itemMapped = mapBackendToFrontend(
        pricingFunction,
        curriedPriceItemOptionModifier
      )(item);

      if (itemMapped !== null) {
        addToCart(itemMapped);
      } else {
        toast.error(formatMessage({ id: 'addToCartError' }, { itemName: item.name }));
      }
    },
    [addToCart, formatMessage, prices, pricingFunction, vendor]
  );

  const routeStoreToInitialLocation = useCallback(() => {
    setStoreLocatorCallbackUrl(location.pathname);
    navigate(routes.storeLocator);
  }, [location.pathname, navigate, setStoreLocatorCallbackUrl]);

  const logAddRecentItemToCart = useCallback(
    (name: string, productPosition: number, numItems: number) => {
      mParticle.logEvent(CustomEventNames.ADD_RECENT_ITEM_TO_CART, EventTypes.UserContent, {
        'Product Name': name,
        'Product Position': productPosition,
        'Number of recent items': numItems,
        'User Location': locationName
      });
    },
    [locationName, mParticle]
  );

  const addAndLogItemToCart = useCallback(
    (item: IBackendCartEntries, index: number) => {
      const { name } = item;
      addItemToCart(item);
      if (name) {
        logAddRecentItemToCart(name, index + 1, recentItems.length);
        setRecentItemIndex(index + 1);
      }

      hapticImpact({ style: HapticsImpactStyle.Heavy });
    },
    [addItemToCart, logAddRecentItemToCart, recentItems.length]
  );

  useEffect(() => {
    if (isStoreOpenAndAvailable && pendingRecentItem) {
      addAndLogItemToCart(pendingRecentItem, recentItemIndex);
      setPendingRecentItem(null);
    }
  }, [
    addAndLogItemToCart,
    isStoreOpenAndAvailable,
    pendingRecentItem,
    recentItemIndex,
    setPendingRecentItem
  ]);

  const handleAddToCart = (item: IBackendCartEntries, index: number) => {
    if (isStoreOpenAndAvailable) {
      setRecentItemIndex(index);
      addAndLogItemToCart(item, index);
    } else {
      setPendingRecentItem(item);
      routeStoreToInitialLocation();
    }
  };
  const uniqueItems = user?.uniquePurchasedItems || [];

  useEffect(() => {
    if (uniqueItems.length) {
      const curriedPriceItemOptionModifier = (item: IBackendCartEntries) => (
        modifier: IBackendCartEntries
      ) =>
        priceForItemOptionModifierPlu({
          item,
          modifier,
          prices,
          vendor
        });

      const cartEntriesMappedToFrontend = uniqueItems.map(
        mapBackendToFrontend(pricingFunction, curriedPriceItemOptionModifier)
      );

      // If prices aren't available, all items will be identified as unavailable
      // instead mark them as available to make sure we never fire the error modal
      if (isEmpty(prices)) {
        const mappedItems = uniqueItems.map(currentItem => ({
          ...currentItem,
          isAvailable: true,
          isDisabled: isOfferType(currentItem.type),
          lineId: ''
        }));
        setRecentItems(mappedItems);
        setIsLoading(false);
        return;
      }
      let mappedVendorConfigs = {} as Record<string, ISanityVendorConfigs>;
      getMenuItemVendorConfigForCartEntries(cartEntriesMappedToFrontend)
        .then(({ items }) => {
          mappedVendorConfigs = items;
          return getUnavailableItemsFromMenu(cartEntriesMappedToFrontend);
        })
        .then(({ items }) => {
          const itemIdsSet = new Set(items.map(item => item._id));
          const recentItemsWithAvail = uniqueItems.reduce(
            (acc: Array<IBackendCartEntriesWithAvailability>, currentItem: IBackendCartEntries) => {
              const isAvailable = !itemIdsSet.has(currentItem.sanityId || currentItem._id);
              acc.push({
                ...currentItem,
                vendorConfigs: mappedVendorConfigs[currentItem.sanityId ?? currentItem._id],
                isAvailable,
                isDisabled: isOfferType(currentItem.type),
                lineId: ''
              });
              return acc;
            },
            []
          );
          setRecentItems(recentItemsWithAvail);
        })
        .finally(() => setIsLoading(false));
    }
  }, [
    getUnavailableItemsFromMenu,
    getMenuItemVendorConfigForCartEntries,
    prices,
    pricingFunction,
    uniqueItems,
    vendor
  ]);
  const handleAddClick = (item: IBackendCartEntriesWithAvailability, index: number) => () =>
    handleAddToCart(item, index);
  const getProductImage = (item: IBackendCartEntriesWithAvailability) => {
    const image = sanityImageMap[item.sanityId || item._id];
    if (image?.asset) {
      return image;
    }
    // If item doesn't have image, check if a picker image exists
    const pickerId = maybeGetPickerIdFromCartEntry(item);
    return sanityImageMap[pickerId];
  };
  const backgroundImage = mainMenu.data?.pickerBackgroundImage;
  const getIsAvailable = ({ isAvailable }: { isAvailable: boolean }) =>
    isStoreOpenAndAvailable ? isAvailable : true;
  return (
    <Container>
      {isLoading ? (
        <LoadingContainer>
          <LoadingAnimation fillColor={Styles.color.primary} />
        </LoadingContainer>
      ) : (
        <RecentOrderCardCarousel bgColor="transparent">
          {recentItems.map((item: IBackendCartEntriesWithAvailability, index: number) =>
            enableRecentItemWithModifiers ? (
              <RecentOrderCardWithModifiers
                key={item.sanityId || item._id}
                handleAddClick={handleAddClick(item, index)}
                item={item}
                productImage={getProductImage(item)}
                backgroundImage={backgroundImage}
                isAvailable={getIsAvailable(item)}
              />
            ) : (
              <RecentOrderCard
                key={item.sanityId || item._id}
                isAvailable={getIsAvailable(item) && !item.isDisabled}
                name={item.name || ''}
                productImage={getProductImage(item)}
                backgroundImage={backgroundImage}
                addToCartOnClick={handleAddClick(item, index)}
                showEdit={enableEditRecentItem}
              />
            )
          )}
        </RecentOrderCardCarousel>
      )}
    </Container>
  );
};

export default RecentItems;
